import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { productUsageService } from '../../services/api';
import useProductUsageData from './useProductUsageData';
import useProductUsageTour from './useProductUsageTour';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import SearchableDropdown from '../common/SearchableDropdown';
import ClearableDatePicker from '../common/ClearableDatePicker';
import ClearableInput from '../common/ClearableInput';
import TablePagination from '../common/TablePagination';
import 'react-datepicker/dist/react-datepicker.css';

const ProductUsageReport = ({ hasSeenNavbarTour }) => {
  const { isAuthenticated, userInfo } = useAuth();
  const isAdmin = userInfo?.role === 'admin';

  // Use custom hook for data management
  const {
    products,
    totalCount,
    pageCount,
    countries,
    profiles,
    loading,
    error,
    pagination,
    setPagination,
    serverFilters,
    filterInputs,
    handleInputChange,
    applyFilters,
    clearAllFilters,
    parseDateString,
    formatDateForAPI
  } = useProductUsageData({ isAuthenticated, isAdmin });

  // Use tour hook
  const { startTour } = useProductUsageTour({
    isAuthenticated,
    dataReady: products.length > 0,
    hasSeenNavbarTour
  });

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const downloadDropdownRef = useRef(null);

  // Sorting state
  const [sorting, setSorting] = useState([]);

  // Close download dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadDropdownRef.current && !downloadDropdownRef.current.contains(event.target)) {
        setShowDownloadDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle download with format option
  const handleDownload = (format) => {
    const url = productUsageService.getDownloadUrl({
      fromDate: serverFilters.fromDate,
      toDate: serverFilters.toDate,
      country: serverFilters.country,
      search: serverFilters.search,
      profile: serverFilters.profile,
      format
    });
    window.location.href = url;
    setShowDownloadDropdown(false);
  };

  // Transform countries for SearchableDropdown
  const countryItems = useMemo(() => {
    const items = [];
    if (countries) {
      countries.forEach(country => {
        items.push({ label: country.value, value: country.key });
      });
    }
    return items;
  }, [countries]);

  // Transform profiles for SearchableDropdown (admin only)
  const profileItems = useMemo(() => {
    const items = [];
    if (profiles) {
      profiles.forEach(profile => {
        items.push({ label: profile.name, value: profile.clientKey });
      });
    }
    return items;
  }, [profiles]);

  // Helper to format quantity with unit
  const formatQuantityWithUnit = (quantity, unit) => {
    const formatted = quantity.toFixed(2);
    return unit ? `${formatted} ${unit}` : formatted;
  };

  // Table columns definition - FeedID column only for admins
  const columns = useMemo(() => {
    const cols = [];

    // FeedID column - admin only
    if (isAdmin) {
      cols.push({
        accessorKey: 'feedId',
        header: 'FEED ID',
        cell: info => info.getValue(),
      });
    }

    // Product Name column
    cols.push({
      accessorKey: 'feedName',
      header: 'PRODUCT NAME',
      cell: info => info.getValue() || '-',
    });

    // Usage Count column
    cols.push({
      accessorKey: 'usageCount',
      header: 'USAGE COUNT',
      cell: info => info.getValue().toLocaleString(),
    });

    // Total Quantity column
    cols.push({
      accessorKey: 'totalQuantity',
      header: 'TOTAL QUANTITY',
      cell: info => formatQuantityWithUnit(info.getValue(), info.row.original.unit),
    });

    // Average Quantity column
    cols.push({
      accessorKey: 'averageQuantity',
      header: 'AVG QUANTITY',
      cell: info => formatQuantityWithUnit(info.getValue(), info.row.original.unit),
    });

    return cols;
  }, [isAdmin]);

  // TanStack Table instance with manual pagination
  const table = useReactTable({
    data: products,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: pageCount,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: {
      pagination,
      sorting,
    },
  });

  return (
    <div className="px-4 py-8">
      {/* Header Section */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Product Usage Report
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            View aggregated product usage data across diet recommendations.
          </p>
        </div>

        {/* Tour Button */}
        <button
          onClick={startTour}
          className="hidden md:flex flex-col items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
          data-intro="tour-button"
        >
          <svg className="w-5 h-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
          <span className="text-xs font-medium">Tour</span>
        </button>
      </div>

      {/* Main Content Section with Loading Overlay */}
      <div className="relative">
        {/* Filter Section Wrapper */}
        <div>
          {/* Filter Toggle Button */}
          <div className={`${showFilters ? 'mb-4' : 'mb-2'} ${isAdmin ? '' : 'lg:flex lg:justify-center'}`}>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors ${isAdmin ? '' : 'lg:ml-[-2.5rem]'}`}
              data-intro="filter-toggle"
            >
          <svg
            className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
        </button>
      </div>

      {/* Filters Section */}
      <div
        className={`bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out ${
          showFilters
            ? 'mb-6 max-h-screen opacity-100 p-4 overflow-visible'
            : 'max-h-0 opacity-0 p-0 overflow-hidden'
        }`}
      >
        <div className={`grid grid-cols-1 gap-4 ${isAdmin ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-3'}`}>
          {/* Search Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {isAdmin ? 'Search (Feed ID or Product Name)' : 'Search by Product Name'}
            </label>
            <ClearableInput
              value={filterInputs.search}
              onChange={(value) => handleInputChange('search', value)}
              onClear={() => handleInputChange('search', '')}
              placeholder={isAdmin ? "Enter Feed ID or product name..." : "Enter product name..."}
            />
          </div>

          {/* Client Filter - Admin Only */}
          {isAdmin && profiles.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Client
              </label>
              <SearchableDropdown
                items={profileItems}
                onSelect={(selectedItem) => handleInputChange('profile', selectedItem.value)}
                onClear={() => handleInputChange('profile', '')}
                buttonPlaceholder="All Clients"
                searchPlaceholder="Search clients..."
                widthClass="w-full"
                selectedValue={filterInputs.profile}
              />
            </div>
          )}

          {/* Country Filter */}
          {countries && countries.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Country
              </label>
              <SearchableDropdown
                items={countryItems}
                onSelect={(selectedItem) => handleInputChange('country', selectedItem.value)}
                onClear={() => handleInputChange('country', '')}
                buttonPlaceholder="All Countries"
                widthClass="w-full"
                selectedValue={filterInputs.country}
                searchable={false}
              />
            </div>
          )}

          {/* From Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              From Date
            </label>
            <ClearableDatePicker
              selected={parseDateString(filterInputs.fromDate)}
              onChange={(date) => handleInputChange('fromDate', formatDateForAPI(date))}
              onClear={() => handleInputChange('fromDate', '')}
              placeholder="Select from date"
              maxDate={parseDateString(filterInputs.toDate)}
            />
          </div>

          {/* To Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              To Date
            </label>
            <ClearableDatePicker
              selected={parseDateString(filterInputs.toDate)}
              onChange={(date) => handleInputChange('toDate', formatDateForAPI(date))}
              onClear={() => handleInputChange('toDate', '')}
              placeholder="Select to date"
              minDate={parseDateString(filterInputs.fromDate)}
            />
          </div>
        </div>

        {/* Filter Action Buttons */}
        <div className="mt-4 flex justify-center space-x-3">
          <button
            onClick={applyFilters}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span>Apply Filters</span>
          </button>
          <button
            onClick={clearAllFilters}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:text-white dark:focus:ring-gray-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <span>Clear All</span>
          </button>
          </div>
        </div>
      </div>

        {/* Download Button - Moved below filters */}
        {!loading && products.length > 0 && (
        <div className="mb-4 flex justify-end">
          <div className="relative" ref={downloadDropdownRef}>
            <button
              onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 rounded-lg dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800"
              data-intro="download-button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Download</span>
              <svg className={`w-3 h-3 transition-transform ${showDownloadDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showDownloadDropdown && (
              <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                <div className="py-1">
                  <button
                    onClick={() => handleDownload('xlsx')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Excel (.xlsx)</span>
                  </button>
                  <button
                    onClick={() => handleDownload('csv')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span>CSV (.csv)</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

        {/* Error State - Retry Component */}
        {error && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              Unable to load data
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {error}
            </p>
            <button
              onClick={applyFilters}
              className="mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
            >
              Try Again
            </button>
          </div>
        )}

      {/* Data Table - Always visible so loading overlay can blur it */}
      {!error && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden" data-intro="product-usage-table">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        scope="col"
                        className="px-6 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center space-x-1">
                          <span>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </span>
                          {{
                            asc: <span className="text-blue-600">↑</span>,
                            desc: <span className="text-blue-600">↓</span>,
                          }[header.column.getIsSorted()] ?? null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      No products found
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <tr
                      key={row.id}
                      className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-6 py-4">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between" data-intro="pagination">
            <TablePagination table={table} totalCount={totalCount} />
          </div>
        </div>
      )}

        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductUsageReport;
